import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Clock, MapPin } from "lucide-react";
import { NAV, LEGAL_LINKS, COMPANY } from "@/lib/content";
import { useSettings } from "@/context/SettingsContext";
import { scrollToId } from "@/lib/nav";

const LOGO = `${process.env.PUBLIC_URL}/amanah-main-logo.png`;

export default function Footer() {
  const { phone, business_hours, public_email } = useSettings();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goSection = (section) => {
    if (section === "top") {
      pathname !== "/" ? navigate("/") : scrollToId("top");
      return;
    }
    pathname !== "/" ? navigate("/", { state: { scrollTo: section } }) : scrollToId(section);
  };

  return (
    <footer className="border-t border-line bg-cream" data-testid="site-footer">
      <div className="h-1 w-full bg-navy" aria-hidden="true" />
      <div className="mx-auto max-w-7xl px-6 py-16 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <img
              src={LOGO}
              alt={`${COMPANY.name} — ${COMPANY.strapline}`}
              className="h-20 w-auto"
              width="220"
              height="156"
            />
            <p className="mt-6 max-w-sm font-display text-lg text-navy">
              Ethical fundraising. Measurable impact.
            </p>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-body">
              A fundraising and promotions agency representing charities with integrity,
              accountability and transparency across {COMPANY.area}.
            </p>
          </div>

          {/* Navigate */}
          <div className="md:col-span-3">
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-muted">
              Navigate
            </h3>
            <ul className="mt-5 space-y-3">
              {NAV.map((item) => (
                <li key={item.label}>
                  {item.section ? (
                    <button
                      onClick={() => goSection(item.section)}
                      className="text-sm text-slate-body transition-colors hover:text-brand-green"
                      data-testid={`footer-nav-${item.section}`}
                    >
                      {item.label}
                    </button>
                  ) : (
                    <Link
                      to={item.to}
                      className="text-sm text-slate-body transition-colors hover:text-brand-green"
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-muted">
              Get in touch
            </h3>
            <ul className="mt-5 space-y-4 text-sm text-slate-body">
              <li className="flex items-start gap-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-brand-green" />
                <a href={`mailto:${public_email}`} className="hover:text-brand-green" data-testid="footer-email">
                  {public_email}
                </a>
              </li>
              {phone && (
                <li className="flex items-start gap-3" data-testid="footer-phone">
                  <span className="mt-0.5 h-4 w-4 shrink-0 text-brand-green">☎</span>
                  <a href={`tel:${phone.replace(/\s+/g, "")}`} className="hover:text-brand-green">
                    {phone}
                  </a>
                </li>
              )}
              <li className="flex items-start gap-3">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-brand-green" />
                <span>{business_hours}</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-green" />
                <span>Operating throughout {COMPANY.area}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 border-t border-line pt-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <ul className="flex flex-wrap gap-x-6 gap-y-2">
              {LEGAL_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-slate-body transition-colors hover:text-brand-green"
                    data-testid={`footer-legal-${l.to.replace("/", "")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-muted">
              © {new Date().getFullYear()} {COMPANY.name}
              <span className="mx-2 text-line" aria-hidden="true">·</span>
              <Link
                to="/site-settings/login"
                className="text-slate-muted/70 transition-colors hover:text-brand-green"
                data-testid="footer-staff-login"
              >
                Staff login
              </Link>
            </p>
          </div>
          <p className="mt-6 max-w-3xl text-xs leading-relaxed text-slate-muted">
            {COMPANY.name} is registered in England and Wales. Company number {COMPANY.number}.
            Registered office: {COMPANY.office}.
          </p>
        </div>
      </div>
    </footer>
  );
}
